import React, { useState } from "react"

import { NotificationManager } from "react-notifications"
import { gql, useMutation } from "urql"

import { Tooltip } from "@chakra-ui/core"
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { Button, StyledLink } from "~tailwindui/Basics"
import Table from "~tailwindui/Table/Table"

import RemoveSubjectBucketModal from "./RemoveSubjectBucketModal"

export type SubjectBucket = { id: string; name: string }

type Tier = {
  id: string
  name: string
  gradeRange: string
  subjectBuckets: SubjectBucket[]
}

export type TutorSubjectsTableProps = {
  tiers: Tier[]
  refetchQuery: VoidFunction
}

const TutorSubjectsTable: React.FC<TutorSubjectsTableProps> = ({
  tiers,
  refetchQuery,
}) => {
  const [subjectBucketToRemove, setSubjectBucketToRemove] =
    useState<SubjectBucket | null>(null)
  const [, runResubmitMutation] = useMutation(resubmitTutorSubject)

  const handleResubmit = subjectBucketId => {
    runResubmitMutation({ subjectBucketId })
      .then(
        response => {
          const { failures } = response.data.resubmitTutorSubject
          if (failures.length > 0)
            NotificationManager.error(failures[0].message)
          else {
            refetchQuery()
            NotificationManager.success("Subject re-submitted for approval")
          }
        },
        () => NotificationManager.error("Something went wrong")
      )
      .catch(() => NotificationManager.error("Something went wrong"))
  }

  const statusIcon = approvalState => {
    switch (approvalState) {
      case "submitted":
        return <ClockIcon className="h-5 text-orange-400" />
      case "approved":
        return <CheckCircleIcon className="h-5 text-green-500" />
      case "rejected":
        return <XCircleIcon className="h-5 text-red-400" />
    }
  }

  return (
    <>
      <RemoveSubjectBucketModal
        subjectBucket={subjectBucketToRemove}
        setSubjectBucket={setSubjectBucketToRemove}
        refetchQuery={refetchQuery}
      />

      {tiers.map(tier => (
        <Table
          key={tier.id}
          title={
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <span>{tier.gradeRange}</span>
              <StyledLink
                key={tier.id}
                to={path("tutorSubjectsEdit", { id: tier.id })}
                button
                size={Size.Small}
              >
                Add/Remove {tier.gradeRange} Subjects
              </StyledLink>
            </div>
          }
          data={tier.subjectBuckets}
          cols={[
            {
              header: "Subject",
              value: subjectBucket => (
                <span
                  key={subjectBucket.id}
                  className={
                    subjectBucket.approvalState === "submitted"
                      ? "italic text-gray-400"
                      : subjectBucket.approvalState === "rejected"
                      ? "text-red-500"
                      : ""
                  }
                >
                  {subjectBucket.name}
                </span>
              ),
            },
            {
              header: "Approved",
              value: subjectBucket => (
                <Tooltip
                  key={subjectBucket.id}
                  label={subjectBucket.approvalState}
                  className="bg-gray-200 p-1"
                  aria-label={subjectBucket.approvalState}
                >
                  <div className="flex items-center gap-2">
                    <span className="sm:hidden">
                      {subjectBucket.approvalState}
                    </span>
                    <div>{statusIcon(subjectBucket.approvalState)}</div>
                  </div>
                </Tooltip>
              ),
            },
            {
              header: "Actions",
              value: subjectBucket => (
                <div key={subjectBucket.id} className="flex space-x-2">
                  <Button
                    soft
                    color={Color.Red}
                    onClick={() => setSubjectBucketToRemove(subjectBucket)}
                  >
                    <Tooltip
                      label="Remove"
                      className="bg-gray-200 p-1"
                      aria-label="Remove"
                    >
                      <TrashIcon className="h-5 text-red-400" />
                    </Tooltip>
                  </Button>

                  {subjectBucket.approvalState === "rejected" && (
                    <Button
                      soft
                      onClick={() => handleResubmit(subjectBucket.id)}
                    >
                      <Tooltip
                        label="Re-Submit"
                        className="bg-gray-200 p-1"
                        aria-label="Re-Submit"
                      >
                        <ArrowPathIcon className="h-5 text-blue-400" />
                      </Tooltip>
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      ))}
    </>
  )
}

const resubmitTutorSubject = gql`
  mutation ResubmitTutorSubject($subjectBucketId: ID!) {
    resubmitTutorSubject(subjectBucketId: $subjectBucketId) {
      failures {
        message
      }
    }
  }
`

export default TutorSubjectsTable
